import {
  foldCueText,
  RECORDING_INSTRUCTION_MAX_LENGTH,
  RECORDING_INSTRUCTIONS_MAX,
} from "@rosc/contracts";
import { create } from "zustand";
import { AppConfig } from "@/common/appConfig";

// Instructions typed during the current recording ("insert normal cardiac
// exam", "keep the plan short"). They go with the visit when it is submitted;
// the API applies them once the note is written. Kept in memory for the open
// recorder only: a new recording starts empty.
interface RecordingInstructionsState {
  items: string[];
  // Saved shortcut names whose spoken cue was dismissed ("Will insert: … ×").
  skipped: string[];
  // False when the text is empty, already listed or the list is full.
  add: (text: string) => boolean;
  remove: (index: number) => void;
  // Replaces one instruction (edit); false when the text is empty or the
  // same as another one.
  update: (index: number, text: string) => boolean;
  skip: (shortcutName: string) => void;
  reset: () => void;
}

export const useRecordingInstructionsStore =
  create<RecordingInstructionsState>()((set, get) => ({
    items: [],
    skipped: [],
    add: (text): boolean => {
      const item = text.trim().slice(0, RECORDING_INSTRUCTION_MAX_LENGTH);
      const { items } = get();
      if (
        !item ||
        items.length >= RECORDING_INSTRUCTIONS_MAX ||
        items.some((other) => other.toLowerCase() === item.toLowerCase())
      )
        return false;
      set({ items: [...items, item] });
      return true;
    },
    remove: (index): void => {
      set({ items: get().items.filter((_, i) => i !== index) });
    },
    update: (index, text): boolean => {
      const item = text.trim().slice(0, RECORDING_INSTRUCTION_MAX_LENGTH);
      const { items } = get();
      if (
        !item ||
        index < 0 ||
        index >= items.length ||
        items.some(
          (other, i) =>
            i !== index && other.toLowerCase() === item.toLowerCase(),
        )
      )
        return false;
      set({ items: items.map((other, i) => (i === index ? item : other)) });
      return true;
    },
    skip: (shortcutName): void => {
      const { skipped } = get();
      const key = foldCueText(shortcutName.trim());
      if (
        !key ||
        skipped.length >= 20 ||
        skipped.some((s) => foldCueText(s) === key)
      )
        return;
      set({ skipped: [...skipped, shortcutName.trim().slice(0, 64)] });
    },
    reset: (): void => {
      set({ items: [], skipped: [] });
    },
  }));

/**
 * The `instructions` field for a new visit. With the feature on it is always
 * sent (an empty list still asks the API to apply the spoken "insert
 * <shortcut>" cue); with it off the field is left out.
 */
export const recordingInstructionsField = (
  {
    items,
    skipped,
  }: {
    items: readonly string[];
    skipped: readonly string[];
  } = useRecordingInstructionsStore.getState(),
): { instructions?: string[]; skipShortcuts?: string[] } =>
  AppConfig.enableRecordingInstructions
    ? {
        instructions: [...items],
        ...(skipped.length ? { skipShortcuts: [...skipped] } : {}),
      }
    : {};
